import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  ALL_PRODUCT_TYPES,
  ChartProductTypes,
  DEFAULT_CHART_PRODUCT,
  ENTITY_NAME_PROPERTY,
  MPC_ON_TIME_SHRINKAGE_INCOMPATIBLE_CHART_TYPES,
  MaterialBalanceTimeChartProducts,
  RecoveryTimeChartProducts,
  SalesProductList
} from "constants/chart.constants";
import { EvaChart, TimeStep } from "constants/charts.enums";
import { useChartsContext } from "contexts";
import { createFeatureEntities } from "entities/charts/eva/features";
import { updateChart, updateChartByPath } from "store/features";
import {
  updateChartProduct,
  updateMultiProductActive,
  updateMultiProductProperty
} from "store/features/charts/mpcActions";
import { RootState } from "store/rootReducer";
import { getProductType } from "utils";
import { getProductKey } from "utils/chart/getProductKey";

import { useChartEntities } from "hooks/charts";
import useFeatureFlags from "hooks/useFeatureFlags";

import { ToastType, dismissChartToast, showChartToast } from "../components/ChartToast";
import { checkIncompatibleProductWithTotalRateDate } from "../components/ProductSelector/utils/checkIncompatibleProductWithTotalRateDate";
import { checkOnTimeProductIsWithCompatibleChartType } from "../components/ProductSelector/utils/checkOnTimeProductIsWithCompatibleChartType";
import { checkProductExists } from "../components/ProductSelector/utils/checkProductExists";
import { checkVrrProductWithCompatibleChartType } from "../components/ProductSelector/utils/checkVrrProductWithCompatibleChartType";

const EMPTY_ARRAY: string[] = [];
const PRESSURE_LABELS = [
  ALL_PRODUCT_TYPES.CasingPressure.label,
  ALL_PRODUCT_TYPES.TubingPressure.label
];
const PRESSURE_KEYS = [
  ALL_PRODUCT_TYPES.CasingPressure.key,
  ALL_PRODUCT_TYPES.TubingPressure.key
];
const ON_TIME_SHRINKAGE_KEYS = [
  ALL_PRODUCT_TYPES.OnTime.key,
  ALL_PRODUCT_TYPES.Shrinkage.key
];
const ON_TIME_SHRINKAGE_LABELS = [
  ALL_PRODUCT_TYPES.OnTime.label,
  ALL_PRODUCT_TYPES.Shrinkage.label
];
const VRR_IVRR_KEYS = [ALL_PRODUCT_TYPES.VRR.key, ALL_PRODUCT_TYPES.IVRR.key];
const VRR_IVRR_LABELS = [ALL_PRODUCT_TYPES.VRR.label, ALL_PRODUCT_TYPES.IVRR.label];
// EVA-10: keys for every product on the Sales tab (see ProductSelector.tsx),
// derived from the same SalesProductList used to identify the Sales tab so
// the two stay in sync.
const SALES_PRODUCT_KEYS = SalesProductList.map(getProductKey);

export const useChartProductHandler = () => {
  const { id, entityKind } = useChartsContext();
  const {
    chartType,
    product,
    features,
    timeStep: timeStepState,
    sum
  } = useChartEntities(id);
  const dispatch = useDispatch();
  const globalGroupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  const { hasFeature } = useFeatureFlags();
  const hasSalesChartProducts = hasFeature("Sales Chart Products");

  const timeStep = timeStepState?.properties?.value;
  const isSpaghettiActive = sum?.active ?? false;
  const isGroupedByEntityName = globalGroupBy?.property === ENTITY_NAME_PROPERTY;
  const isMultiProductActive = features?.multiProduct?.active || false;
  const otherProducts = features?.multiProduct?.properties?.otherProducts ?? EMPTY_ARRAY;
  const primaryProducts =
    features?.multiProduct?.properties?.primaryProducts ?? EMPTY_ARRAY;
  const secondaryProducts =
    features?.multiProduct?.properties?.secondaryProducts ?? EMPTY_ARRAY;

  const isPressureDisabled =
    (chartType !== EvaChart.RateTime &&
      chartType !== EvaChart.RateCum &&
      chartType !== EvaChart.RateDate) ||
    (timeStep !== TimeStep.Hybrid && timeStep !== TimeStep.Day) ||
    (chartType === EvaChart.RateDate && !isSpaghettiActive && !isGroupedByEntityName);

  useEffect(() => {
    // display "rate time" chart if "rate cum" or "cum time" or "total rate cum" is selected for on time product
    if (
      checkOnTimeProductIsWithCompatibleChartType(chartType, product) &&
      !isMultiProductActive
    ) {
      dispatch(
        updateChartByPath({
          id,
          path: "chartType",
          value: EvaChart.RateTime
        })
      );
    }

    // In MP mode, On-Time and Shrinkage are incompatible with Rate Cum / Cum Time / Total Rate Cum.
    // Check otherProducts directly since the primary product label may not reflect all selected products.
    const isOnTimeShrinkageIncompatibleChartType =
      MPC_ON_TIME_SHRINKAGE_INCOMPATIBLE_CHART_TYPES.includes(chartType);

    if (isMultiProductActive && isOnTimeShrinkageIncompatibleChartType) {
      const filteredOtherProducts = otherProducts.filter(
        (p) => !ON_TIME_SHRINKAGE_KEYS.includes(p)
      );

      // Only act if there were incompatible products to remove
      if (filteredOtherProducts.length < otherProducts.length) {
        if (filteredOtherProducts.length === 0) {
          // All selected products were incompatible — disable MP and fall back to Rate Time
          dispatch(
            updateChartByPath({ id, path: "chartType", value: EvaChart.RateTime })
          );
          dispatch(updateMultiProductActive(id, false));

          // Set product label to the surviving (incompatible) product before clearing the lists.
          const fallbackProductKey = otherProducts[0];
          const fallbackProduct = Object.values(ALL_PRODUCT_TYPES).find(
            (p) => p.key === fallbackProductKey
          );
          if (fallbackProduct) {
            dispatch(updateChartProduct(id, fallbackProduct.label));
          }

          dispatch(updateMultiProductProperty(id, "otherProducts", []));
          dispatch(updateMultiProductProperty(id, "primaryProducts", []));
          dispatch(updateMultiProductProperty(id, "secondaryProducts", []));
          dispatch(updateMultiProductProperty(id, "productLineStyles", {}));
          dispatch(updateMultiProductProperty(id, "overrideLineStyles", {}));
        } else {
          // Compatible products remain — remove the incompatible ones and stay on the current chart type
          const filteredPrimaryProducts = primaryProducts.filter(
            (p) => !ON_TIME_SHRINKAGE_KEYS.includes(p)
          );
          const filteredSecondaryProducts = secondaryProducts.filter(
            (p) => !ON_TIME_SHRINKAGE_KEYS.includes(p)
          );
          const resolvedPrimaryProducts =
            filteredPrimaryProducts.length > 0
              ? filteredPrimaryProducts
              : [filteredOtherProducts[0]];

          dispatch(
            updateMultiProductProperty(id, "otherProducts", filteredOtherProducts)
          );
          dispatch(
            updateMultiProductProperty(id, "primaryProducts", resolvedPrimaryProducts)
          );
          dispatch(
            updateMultiProductProperty(id, "secondaryProducts", filteredSecondaryProducts)
          );

          // If the primary product label was itself incompatible, update it to the first remaining product
          if (ON_TIME_SHRINKAGE_LABELS.includes(product)) {
            const replacementProduct = Object.values(ALL_PRODUCT_TYPES).find(
              (p) => p.key === resolvedPrimaryProducts[0]
            );
            if (replacementProduct) {
              dispatch(updateChartProduct(id, replacementProduct.label));
            }
          }
        }
      }
    }

    // In MPC mode, VRR and IVRR are only valid on Rate Date. Remove them when switching to any other chart type.
    if (isMultiProductActive && chartType !== EvaChart.RateDate) {
      const filteredOtherProducts = otherProducts.filter(
        (p) => !VRR_IVRR_KEYS.includes(p)
      );

      // Only act if there were incompatible products to remove
      if (filteredOtherProducts.length < otherProducts.length) {
        if (filteredOtherProducts.length === 0) {
          // All selected products were VRR/IVRR — disable MPC and stay on the current chart type
          dispatch(updateMultiProductActive(id, false));

          // Set product label to the first removed product (or fallback to Oil)
          const fallbackProductKey = otherProducts[0];
          const fallbackProduct = Object.values(ALL_PRODUCT_TYPES).find(
            (p) => p.key === fallbackProductKey
          );
          dispatch(
            updateChartProduct(
              id,
              fallbackProduct ? fallbackProduct.label : ALL_PRODUCT_TYPES.Oil.label
            )
          );

          dispatch(updateMultiProductProperty(id, "otherProducts", []));
          dispatch(updateMultiProductProperty(id, "primaryProducts", []));
          dispatch(updateMultiProductProperty(id, "secondaryProducts", []));
          dispatch(updateMultiProductProperty(id, "productLineStyles", {}));
          dispatch(updateMultiProductProperty(id, "overrideLineStyles", {}));
        } else {
          // Compatible products remain — remove only the VRR/IVRR ones
          const filteredPrimaryProducts = primaryProducts.filter(
            (p) => !VRR_IVRR_KEYS.includes(p)
          );
          const filteredSecondaryProducts = secondaryProducts.filter(
            (p) => !VRR_IVRR_KEYS.includes(p)
          );
          const resolvedPrimaryProducts =
            filteredPrimaryProducts.length > 0
              ? filteredPrimaryProducts
              : [filteredOtherProducts[0]];

          dispatch(
            updateMultiProductProperty(id, "otherProducts", filteredOtherProducts)
          );
          dispatch(
            updateMultiProductProperty(id, "primaryProducts", resolvedPrimaryProducts)
          );
          dispatch(
            updateMultiProductProperty(id, "secondaryProducts", filteredSecondaryProducts)
          );

          // If the primary product label was itself VRR/IVRR, update it to the first remaining product
          if (VRR_IVRR_LABELS.includes(product)) {
            const replacementProduct = Object.values(ALL_PRODUCT_TYPES).find(
              (p) => p.key === resolvedPrimaryProducts[0]
            );
            if (replacementProduct) {
              dispatch(updateChartProduct(id, replacementProduct.label));
            }
          }
        }

        const vrrIvrrRemovedWarning = `vrr-ivrr-mpc-removed-warning-${id}`;
        dismissChartToast(id, vrrIvrrRemovedWarning);
        showChartToast(
          id,
          "VRR and IVRR are only available on the Rate Date chart. They have been removed from the selected products.",
          ToastType.Warning,
          { toastId: vrrIvrrRemovedWarning }
        );
      }
    }

    // display "rate date" chart if "total rate date" is selected for ratios/percentages/yields/HeatingValue/Shrinkage
    if (checkIncompatibleProductWithTotalRateDate(chartType, product)) {
      const targetChartType =
        chartType === EvaChart.TotalRateDateMidstream
          ? EvaChart.RateDateMidstream
          : EvaChart.RateDate;
      const features = createFeatureEntities(targetChartType, {});

      dispatch(
        updateChart({
          id,
          chartType: targetChartType,
          features
        })
      );

      const totalRateDateUnsupportedProductWarning = `total-rate-date-unsupported-warning-${id}`;
      dismissChartToast(id, totalRateDateUnsupportedProductWarning);
      showChartToast(
        id,
        "Total Rate Date not supported for ratio products or percentages. Showing Rate Date instead.",
        ToastType.Warning,
        {
          toastId: totalRateDateUnsupportedProductWarning
        }
      );
    }

    // VRR/IVRR are only valid on Rate Date charts; if navigating away, default product to Oil + Cond
    if (checkVrrProductWithCompatibleChartType(chartType, product)) {
      dispatch(
        updateChartByPath({
          id,
          path: "product",
          value: ALL_PRODUCT_TYPES.Oil.label
        })
      );

      // Only show the toast when the destination chart has a product selector.
      // RF vs Time restricts to Oil/Gas via its own clamp, so suppress the
      // VRR-specific message there to avoid a misleading toast.
      if (ChartProductTypes.includes(chartType) && chartType !== EvaChart.RecoveryTime) {
        const vrrUnsupportedChartTypeWarning = `vrr-unsupported-chart-type-warning-${id}`;
        dismissChartToast(id, vrrUnsupportedChartTypeWarning);
        showChartToast(
          id,
          "VRR and IVRR are only available on the Rate Date chart. Defaulting to Oil + Cond",
          ToastType.Warning,
          {
            toastId: vrrUnsupportedChartTypeWarning
          }
        );
      }
    }

    // Switch product to oil if the selected product isn't compatible with material balance
    if (
      chartType === EvaChart.MaterialBalanceTime &&
      !MaterialBalanceTimeChartProducts.includes(product)
    ) {
      dispatch(
        updateChartByPath({
          id,
          path: "product",
          value: MaterialBalanceTimeChartProducts[0]
        })
      );
    }

    // RF vs Time only supports Oil + Cond and Gas; clamp stale incompatible product to Oil
    if (
      chartType === EvaChart.RecoveryTime &&
      !RecoveryTimeChartProducts.includes(product)
    ) {
      dispatch(
        updateChartByPath({
          id,
          path: "product",
          value: ALL_PRODUCT_TYPES.Oil.label
        })
      );
    }

    if (checkProductExists(product, entityKind)) {
      const updatedProduct = getProductType(product, entityKind).label;
      dispatch(
        updateChartByPath({
          id,
          path: "product",
          value: updatedProduct
        })
      );
    }
  }, [product, chartType, dispatch]);

  useEffect(() => {
    if (!isPressureDisabled) return;

    if (!isMultiProductActive) {
      if (PRESSURE_LABELS.includes(product)) {
        dispatch(
          updateChartByPath({ id, path: "product", value: ALL_PRODUCT_TYPES.Oil.label })
        );
      }
      return;
    }

    const pressureInList = otherProducts.filter((p) => PRESSURE_KEYS.includes(p));
    if (pressureInList.length === 0) return;

    const nonPressureProducts = otherProducts.filter((p) => !PRESSURE_KEYS.includes(p));
    if (nonPressureProducts.length === 0) {
      const replacement = [ALL_PRODUCT_TYPES.Oil.key];
      dispatch(
        updateChart({
          id,
          features: {
            ...features,
            multiProduct: {
              ...features.multiProduct,
              properties: {
                ...features.multiProduct?.properties,
                otherProducts: replacement,
                primaryProducts: replacement,
                secondaryProducts: []
              }
            }
          }
        })
      );
    } else {
      dispatch(
        updateChart({
          id,
          features: {
            ...features,
            multiProduct: {
              ...features.multiProduct,
              properties: {
                ...features.multiProduct?.properties,
                otherProducts: nonPressureProducts,
                primaryProducts: primaryProducts.filter(
                  (p) => !PRESSURE_KEYS.includes(p)
                ),
                secondaryProducts: secondaryProducts.filter(
                  (p) => !PRESSURE_KEYS.includes(p)
                )
              }
            }
          }
        })
      );
    }
  }, [
    timeStep,
    product,
    chartType,
    isSpaghettiActive,
    isGroupedByEntityName,
    isMultiProductActive,
    otherProducts,
    primaryProducts,
    secondaryProducts
  ]);

  // EVA-10: a chart persisted (sessionStorage / saved workspace) with a
  // Sales-tab product (see ProductSelector.tsx / SalesProductList) would
  // otherwise keep rendering/requesting that product even after "Sales Chart
  // Products" has been disabled and the Sales tab is hidden from the
  // selector. Mirrors the pressure clamp above: reset to the entity default
  // in single-product mode, or prune Sales products from the multi-product
  // lists.
  useEffect(() => {
    if (hasSalesChartProducts) return;

    if (!isMultiProductActive) {
      if (SalesProductList.includes(product)) {
        dispatch(
          updateChartByPath({ id, path: "product", value: DEFAULT_CHART_PRODUCT })
        );
      }
      return;
    }

    const salesInList = otherProducts.filter((p) => SALES_PRODUCT_KEYS.includes(p));
    if (salesInList.length === 0) return;

    const nonSalesProducts = otherProducts.filter((p) => !SALES_PRODUCT_KEYS.includes(p));
    if (nonSalesProducts.length === 0) {
      const replacement = [ALL_PRODUCT_TYPES.Oil.key];
      dispatch(
        updateChart({
          id,
          features: {
            ...features,
            multiProduct: {
              ...features.multiProduct,
              properties: {
                ...features.multiProduct?.properties,
                otherProducts: replacement,
                primaryProducts: replacement,
                secondaryProducts: []
              }
            }
          }
        })
      );
    } else {
      dispatch(
        updateChart({
          id,
          features: {
            ...features,
            multiProduct: {
              ...features.multiProduct,
              properties: {
                ...features.multiProduct?.properties,
                otherProducts: nonSalesProducts,
                primaryProducts: primaryProducts.filter(
                  (p) => !SALES_PRODUCT_KEYS.includes(p)
                ),
                secondaryProducts: secondaryProducts.filter(
                  (p) => !SALES_PRODUCT_KEYS.includes(p)
                )
              }
            }
          }
        })
      );
    }
  }, [
    hasSalesChartProducts,
    product,
    isMultiProductActive,
    otherProducts,
    primaryProducts,
    secondaryProducts,
    features,
    dispatch,
    id
  ]);
};
